import * as React from "react";

import { Button, Grid, Table, InputOnChangeData, Input, Message, Icon } from "semantic-ui-react";
import * as moment from "moment";

import { debounce } from "lodash";

import { debounceDelay } from "@teamprevent/common/filtering/constants";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { EmployeeRecordDetailModel } from "../model";
import { employeeRecordsSelector } from "../selectors";

import { ApplicationState } from "../../../applicationState";

import { loadCustomerEmployeeRecords } from "../../customer/actions";

import { Link, replace } from "redux-little-router";

export const All: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const customerId = useSelector((state: ApplicationState) => state.router.params.customerId);
    const path = useSelector((state: ApplicationState) => state.router.pathname!);
    const employeeRecords = useSelector(employeeRecordsSelector).employeeRecords.sort(
        (a, b) => a.lastName.localeCompare(b.lastName)) as EmployeeRecordDetailModel[];
    const [firstName, setFirstName] = React.useState(useSelector((state: ApplicationState) => state.router.query?.firstName || ""));
    const [lastName, setLastName] = React.useState(useSelector((state: ApplicationState) => state.router.query?.lastName || ""));
    const [personalReferenceNumber, setPersonalReferenceNumber] =
        React.useState(useSelector((state: ApplicationState) => state.router.query?.personalReferenceNumber || ""));
    const [dateOfBirth, setDateOfBirth] = React.useState(useSelector((state: ApplicationState) => state.router.query?.dateOfBirth || ""));
    const [secondaryReference, setSecondaryReference] = React.useState(useSelector((state: ApplicationState) => state.router.query?.secondaryReference || ""));
    const tooManyRecords = useSelector(employeeRecordsSelector).tooManyRecords;

    const debouncedUpdateFirstName = debounce((value: string) => dispatch(replace({ query: { firstName: value } }, { persistQuery: true })), debounceDelay);
    const debouncedUpdateLastName = debounce((value: string) => dispatch(replace({ query: { lastName: value } }, { persistQuery: true })), debounceDelay);
    const debouncedUpdatePersonalReferenceNumber =
        debounce((value: string) => dispatch(replace({ query: { personalReferenceNumber: value } }, { persistQuery: true })), debounceDelay);
    const debouncedUpdateDateOfBirth = debounce((value: string) => dispatch(replace({ query: { dateOfBirth: value } }, { persistQuery: true })), debounceDelay);
    const debouncedUpdateSecondaryReference =
        debounce((value: string) => dispatch(replace({ query: { secondaryReference: value } }, { persistQuery: true })), debounceDelay);

    const onFilterFirstNameChange = React.useCallback((_: any, { value }: InputOnChangeData) => {
        setFirstName(value);
        debouncedUpdateFirstName(value);
    }, [debouncedUpdateFirstName]);

    const onFilterLastNameChange = React.useCallback((_: any, { value }: InputOnChangeData) => {
        setLastName(value);
        debouncedUpdateLastName(value);
    },[debouncedUpdateLastName]);

    const onFilterPersonalReferenceNumberChange = React.useCallback((_: any, { value }: InputOnChangeData) => {
        setPersonalReferenceNumber(value);
        debouncedUpdatePersonalReferenceNumber(value);
    }, [debouncedUpdatePersonalReferenceNumber]);

    const onFilterDateOfBirthChange = React.useCallback((_: any, { value }: InputOnChangeData) => {
        setDateOfBirth(value);
        if (!value || (value?.length === 10 && moment(value, "DD/MM/yyyy", true).isValid()))
        {
            debouncedUpdateDateOfBirth(value);
        }
    }, [debouncedUpdateDateOfBirth]);

    const onFilterSecondaryReferenceChange = React.useCallback((_: any, { value }: InputOnChangeData) => {
        setSecondaryReference(value);
        debouncedUpdateSecondaryReference(value);
    }, [debouncedUpdateSecondaryReference]);

    React.useEffect(() => {
        dispatch(loadCustomerEmployeeRecords({ customerId, firstName, lastName, personalReferenceNumber, dateOfBirth, secondaryReference }));
    }, [customerId, dateOfBirth, dispatch, firstName, lastName, personalReferenceNumber, secondaryReference]);

    return (
        <Grid container className="nomargintop">
            <Grid.Row>
                <Grid.Column width={14}>
                    <h1>Employee Records</h1>
                </Grid.Column>
                <Grid.Column width={2} textAlign="right">
                    <Button icon="add" content="Create" as={Link} href={`${path}/create`} />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width={16}>
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>First Name</Table.HeaderCell>
                                <Table.HeaderCell>Last Name</Table.HeaderCell>
                                <Table.HeaderCell>Date Of Birth</Table.HeaderCell>
                                <Table.HeaderCell>Personal Reference Number</Table.HeaderCell>
                                <Table.HeaderCell>Unique ID</Table.HeaderCell>
                                <Table.HeaderCell>Active</Table.HeaderCell>
                            </Table.Row>
                            <Table.Row>
                                <Table.HeaderCell>
                                    <Input
                                        value={firstName}
                                        onChange={onFilterFirstNameChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    <Input
                                        value={lastName}
                                        onChange={onFilterLastNameChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    <Input
                                        value={dateOfBirth}
                                        onChange={onFilterDateOfBirthChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    <Input
                                        value={personalReferenceNumber}
                                        onChange={onFilterPersonalReferenceNumberChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    <Input
                                        value={secondaryReference}
                                        onChange={onFilterSecondaryReferenceChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {employeeRecords && employeeRecords.map(employeeRecord => (
                                <Table.Row key={employeeRecord.id}>
                                    <Table.Cell>
                                        <Link href={`${path}/${employeeRecord.id}`}>
                                            {employeeRecord.firstName}
                                        </Link>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Link href={`${path}/${employeeRecord.id}`}>
                                            {employeeRecord.lastName}
                                        </Link>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Link href={`${path}/${employeeRecord.id}`}>
                                            {employeeRecord.dateOfBirth && employeeRecord.dateOfBirth.format("DD/MM/yyyy")}
                                        </Link>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Link href={`${path}/${employeeRecord.id}`}>
                                            {employeeRecord.personalReferenceNumber}
                                        </Link>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Link href={`${path}/${employeeRecord.id}`}>
                                            {employeeRecord.secondaryReference}
                                        </Link>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Link href={`${path}/${employeeRecord.id}`}>
                                            {employeeRecord.active !== null && employeeRecord.active !== undefined && employeeRecord.active.toString()}
                                        </Link>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                        {tooManyRecords &&
                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell colSpan="4">
                                        <Message attached="bottom" info>
                                            <Icon name="info" />
                                            Too many results. Please narrow your search
                                            {(firstName
                                                || lastName
                                                || dateOfBirth
                                                || personalReferenceNumber
                                                || secondaryReference) && " further"}.
                                        </Message>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        }
                    </Table>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};
